import React, { useMemo, useState } from 'react';
import { useNode, BuilderComponent, getFontStyle, useCanUpgrade, Account, useEditor } from '@mnz/builder';
import { ImageGalleryProps, ImageGallerySetting } from './ImageGallerySetting';
import { Box } from '@mui/material';
import { getBorderCSS, hoverAnimationCss } from '../../common/style';
import { px2rem } from '../../core';
import ImageModal from './ImageModal';
import { Swiper, SwiperSlide } from 'swiper/react';

export const ImageGallery: BuilderComponent<ImageGalleryProps> = ({
    designSettings,
    images,
    title,
    showTitle,
    account,
}) => {
    const {
        connectors: { connect, drag },
        query: { getOptions },
        hidden,
        id,
    } = useNode((node) => ({
        hidden: node.data.hidden,
    }));
    const {
        actions: { selectNode },
    } = useEditor();
    const canEdit = getOptions().enabled;

    const [isOpen, setIsOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState<number | null>(null);
    const showBlock = !hidden && (title || images.length > 0);
    const canUpgrade = useCanUpgrade({ account: account as Account });

    const showImages = useMemo(() => {
        const tmpImages = [...images];
        if (canUpgrade) {
            return tmpImages.splice(0, 4);
        }
        return tmpImages;
    }, [canUpgrade, images]);

    return (
        <>
            {showBlock && (
                <Box
                    sx={{
                        marginTop: px2rem(16),
                        marginX: px2rem(35),
                        ...hoverAnimationCss({ content: '"Image Gallery"', active: canEdit }),
                    }}
                >
                    <Box
                        ref={(ref: HTMLElement) => connect(drag(ref))}
                        sx={{
                            ...getBorderCSS({
                                corner: designSettings.styles.corner,
                                color: designSettings.colors.button.color,
                                buttonStyle: designSettings.styles.button,
                                maxBorderRadius: 1.5,
                            }),
                            cursor: canEdit ? 'pointer' : 'inherit',
                            padding: `${px2rem(10)}`,
                            overflow: 'hidden',
                        }}
                    >
                        {showTitle && title && (
                            <Box
                                sx={{
                                    ...getFontStyle(designSettings, 'secondary', 'bold'),
                                    color: designSettings.colors.buttonText.color,
                                    flexGrow: 1,
                                    textAlign: 'center',
                                    marginLeft: px2rem(10),
                                    marginRight: px2rem(10),
                                    marginBottom: images.length > 0 ? `${px2rem(10)}` : 0,
                                }}
                            >
                                {title}
                            </Box>
                        )}
                        {showImages.length > 0 && (
                            <Box
                                sx={{
                                    ...getBorderCSS({
                                        corner: designSettings.styles.corner,
                                        maxBorderRadius: 1,
                                    }),
                                    overflow: 'hidden',
                                }}
                            >
                                <Swiper
                                    initialSlide={0}
                                    slidesPerView={`auto`}
                                    spaceBetween={10}
                                    centerInsufficientSlides
                                    style={{ width: 'fit-content' }}
                                >
                                    {showImages.map((image, idx) => (
                                        <SwiperSlide key={image.url}>
                                            <Box
                                                sx={{
                                                    ...getBorderCSS({
                                                        corner: designSettings.styles.corner,
                                                        maxBorderRadius: 1,
                                                    }),
                                                    flexShrink: 0,
                                                    width: 110,
                                                    cursor: 'pointer',
                                                    overflow: 'hidden',
                                                }}
                                                onClick={() => {
                                                    if (!canEdit) {
                                                        setIsOpen(true);
                                                        setSelectedImage(idx);
                                                    } else {
                                                        selectNode(id);
                                                    }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        paddingTop: '100%',
                                                        position: 'relative',
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 0,
                                                            left: 0,
                                                            bottom: 0,
                                                            right: 0,
                                                            overflow: 'hidden',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <img
                                                            src={image.url}
                                                            style={{
                                                                objectFit: 'cover',
                                                                height: '100%',
                                                                width: '100%',
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
            {!canEdit && (
                <ImageModal
                    images={showImages}
                    open={isOpen}
                    selectedImage={selectedImage}
                    designSettings={designSettings}
                    onClose={() => {
                        setIsOpen(false);
                        setSelectedImage(null);
                    }}
                />
            )}
        </>
    );
};

ImageGallery.defaultProps = {};

ImageGallery.settings = {
    displayName: 'Image Gallery',
    related: {
        settings: ImageGallerySetting,
    },
    rules: {
        canDrag: () => false,
    },
    custom: {
        showBox: false,
    },
};
